import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { categories } from '@/mocks/categories';

const principles = [
  {
    id: 'principle-source',
    title: '来源优先',
    text: '每一段关键表述都能回溯到具体的公开文件或可验证记录。没有来源的传闻，我们宁可不写。',
    icon: 'ri-links-line',
  },
  {
    id: 'principle-cross',
    title: '交叉核对',
    text: '撰稿与复核由不同的人完成。金额、日期、机构名称这三类最容易出错的要素，必须至少两个来源互相印证。',
    icon: 'ri-shield-check-line',
  },
  {
    id: 'principle-uncertain',
    title: '存疑标注',
    text: '存在但无法证实的部分，我们不会删掉，也不会当成结论，而是明确写成「尚待证实」，把判断空间留给读者。',
    icon: 'ri-error-warning-line',
  },
  {
    id: 'principle-update',
    title: '持续更新',
    text: '事件会演变，稿件也会。重要档案出现新进展时，我们在原文顶部标注更新日期，旧内容保留不改。',
    icon: 'ri-refresh-line',
  },
];

const timeline = [
  { year: '起步', text: '以整理公开监管文件的形式，开始记录美日两地政商争议事件。' },
  { year: '栏目化', text: '把零散稿件归入六大栏目，形成可检索、可追踪的内容结构。' },
  { year: '机制化', text: '建立撰稿与复核分离的流程，并引入编号比对、时间线复盘等核查方法。' },
  { year: '现在', text: '保持每周稳定更新，并把读者提供的线索纳入正式核查队列。' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 | 美日吃瓜 政经暗面观察站',
    description:
      '美日吃瓜是一个专注美国与日本政商圈的深度内容站点，以公开记录为基础整理政客丑闻、财阀内幕、官员贪腐、选举黑幕、企业造假与学术不端六类档案。',
    keywords: '美日吃瓜关于我们,政经暗面观察站,内容原则,核查流程,团队定位',
    path: '/about',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'AboutPage',
          name: '关于美日吃瓜',
          url: '/about',
          inLanguage: 'zh-CN',
          description: '美日吃瓜的定位、内容原则与工作方式说明。',
        },
        {
          '@type': 'Organization',
          name: '美日吃瓜',
          url: '/',
          email: 'editor@meirichigua.com',
          description: '面向中文读者的美日政经深度内容站点。',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: '/about' },
          ],
        },
      ],
    },
  });

  return (
    <>
      <Breadcrumbs items={[{ label: '关于我们' }]} />

      <section className="border-b border-background-300/70 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:items-center">
            <div className="lg:col-span-2">
              <p className="mb-3 font-label text-xs uppercase tracking-[0.32em] text-primary-600">
                About Us
              </p>
              <h1 className="max-w-3xl font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
                我们不是搬运新闻，而是把线索整理成可以核对的档案
              </h1>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                美日吃瓜成立于一个很朴素的判断：政经圈的争议事件，往往不是缺少爆料，而是缺少把碎片拼起来的耐心。我们做的事情很枯燥——把公开文件、时间线与资金流向放在同一张表上比对，然后把对不上的地方写清楚。
              </p>
            </div>
            <div className="rounded-lg border border-background-300/70 bg-background-200/60 p-6">
              <p className="font-heading text-sm font-bold text-foreground-950">站点定位</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                面向中文读者的美日政经深度内容站点，以可追溯来源为底线，服务希望看到完整链条而非结论式标题的读者。
              </p>
              <div className="mt-5 border-t border-background-300/70 pt-4">
                <p className="font-label text-xs text-foreground-500">联络邮箱</p>
                <p className="mt-1 font-heading text-sm font-bold text-foreground-950">
                  editor@meirichigua.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
        <div className="mb-8 max-w-2xl">
          <p className="mb-2 font-label text-xs uppercase tracking-[0.28em] text-primary-600">
            Principles
          </p>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            四条内容原则
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            这些规则不是宣传语，而是我们判断一篇稿子能不能发出去的实际标准。
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {principles.map((principle) => (
            <div
              key={principle.id}
              className="flex gap-4 rounded-lg border border-background-300/70 bg-background-100 p-5"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700">
                <i className={principle.icon} />
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  {principle.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{principle.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-background-300/70 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div>
              <p className="mb-2 font-label text-xs uppercase tracking-[0.28em] text-primary-600">
                Development
              </p>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                我们是怎么走到今天的
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                从一个只整理监管文件的个人笔记，逐渐变成有固定栏目与核查流程的内容站点。
              </p>
            </div>
            <ol className="lg:col-span-2">
              {timeline.map((item, index) => (
                <li key={item.year} className="flex gap-5 border-b border-background-300/70 py-5 last:border-b-0">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-label text-xs font-semibold text-secondary-900">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {item.year}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
        <div className="mb-8 max-w-2xl">
          <p className="mb-2 font-label text-xs uppercase tracking-[0.28em] text-primary-600">
            Coverage
          </p>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            六个固定栏目
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            所有稿件都归入以下栏目之一，方便按线索类型追踪同一主题的持续进展。
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <Link
              key={category.slug}
              to={`/category/${category.slug}`}
              className="group flex items-center gap-4 rounded-lg border border-background-300/70 bg-background-100 p-4 transition-colors hover:border-primary-300"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                <i className={category.icon} />
              </span>
              <div className="min-w-0">
                <h3 className="font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {category.name}
                </h3>
                <p className="mt-0.5 font-label text-xs text-foreground-500">
                  已整理 {category.count} 篇
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-5 rounded-lg bg-secondary-900 p-6 md:flex-row md:items-center md:p-8">
          <div>
            <h2 className="font-heading text-lg font-bold text-background-50">
              想和我们一起核对一条线索？
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-secondary-200">
              无论你是研究者、从业者还是普通读者，只要手里有能佐证的材料，都欢迎提交。
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 font-label text-sm font-medium text-accent-950 transition-colors hover:bg-accent-400"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-send-plane-line" />
            </span>
            前往联系页面
          </Link>
        </div>
      </section>
    </>
  );
}